// Google Analytics 4 Integration
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

export interface AnalyticsEvent {
  event_name: string;
  event_category?: string;
  event_label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

export interface ConversionEvent {
  conversion_id: string;
  conversion_label: string;
  value?: number;
  currency?: string;
  user_data?: {
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
  };
}

class AnalyticsService {
  private gaId: string = import.meta.env.VITE_GA_MEASUREMENT_ID || '';
  private fbPixelId: string = import.meta.env.VITE_META_PIXEL_ID || '';
  private isInitialized: boolean = false;

  constructor() {
    this.initializeAnalytics();
  }

  private initializeAnalytics() {
    if (typeof window === 'undefined' || this.isInitialized) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function() {
      window.dataLayer.push(arguments);
    };

    // Defer all analytics initialization to avoid blocking main thread
    // Use requestIdleCallback with longer timeout for non-critical tracking
    if ('requestIdleCallback' in window) {
      if (this.gaId) requestIdleCallback(() => {
        this.initializeGA4();
      }, { timeout: 5000 });
      
      if (this.fbPixelId) requestIdleCallback(() => {
        this.initializeFacebookPixel();
      }, { timeout: 5000 });
    } else {
      // Fallback with staggered timeouts to distribute load
      if (this.gaId) setTimeout(() => this.initializeGA4(), 3000);
      if (this.fbPixelId) setTimeout(() => this.initializeFacebookPixel(), 4000);
    }
    
    this.isInitialized = true;
  }

  private initializeGA4() {
    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag
    window.gtag('js', new Date());
    window.gtag('config', this.gaId, {
      send_page_view: true,
      cookie_flags: 'SameSite=None;Secure',
      enhanced_measurement: {
        scrolls: true,
        outbound_clicks: true,
        site_search: true,
        video_engagement: true,
        file_downloads: true
      }
    });
  }

  private initializeFacebookPixel() {
    // Facebook Pixel initialization
    (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', this.fbPixelId);
    window.fbq('track', 'PageView');
  }

  // Page tracking
  trackPageView(path: string, title?: string) {
    if (typeof window === 'undefined') return;

    // GA4 page view (check if loaded)
    if (window.gtag) {
      window.gtag('config', this.gaId, {
        page_path: path,
        page_title: title
      });
    }

    // Facebook Pixel page view (check if loaded)
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }

  // Event tracking
  trackEvent(event: AnalyticsEvent) {
    if (typeof window === 'undefined') return;

    // GA4 event (check if loaded)
    if (window.gtag) {
      window.gtag('event', event.event_name, {
        event_category: event.event_category,
        event_label: event.event_label,
        value: event.value,
        ...event.custom_parameters
      });
    }

    // Facebook Pixel custom event (check if loaded)
    if (window.fbq) {
      window.fbq('trackCustom', event.event_name, {
        category: event.event_category,
        label: event.event_label,
        value: event.value,
        ...event.custom_parameters
      });
    }
  }

  // Conversion tracking
  trackConversion(conversion: ConversionEvent) {
    if (typeof window === 'undefined') return;

    // GA4 conversion (check if loaded)
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: `${this.gaId}/${conversion.conversion_label}`,
        value: conversion.value,
        currency: conversion.currency || 'USD',
        ...conversion.user_data
      });
    }

    // Facebook Pixel conversion (check if loaded)
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        value: conversion.value,
        currency: conversion.currency || 'USD',
        content_name: conversion.conversion_id
      });
    }
  }

  // Lead tracking
  trackLead(leadData: {
    source: string;
    type: 'quote_request' | 'roi_calculator' | 'newsletter' | 'contact_form' | 'phone_call';
    value?: number;
    email?: string;
    phone?: string;
    service_interest?: string;
  }) {
    this.trackEvent({
      event_name: 'generate_lead',
      event_category: 'lead_generation',
      event_label: leadData.type,
      value: leadData.value || 100, // Default lead value
      custom_parameters: {
        lead_source: leadData.source,
        lead_type: leadData.type,
        service_interest: leadData.service_interest
      }
    });

    this.trackConversion({
      conversion_id: `lead_${leadData.type}`,
      conversion_label: leadData.type,
      value: leadData.value || 100,
      user_data: {
        email: leadData.email,
        phone: leadData.phone
      }
    });
  }

  // Form tracking
  trackFormSubmission(formData: {
    form_name: string;
    form_location: string;
    success: boolean;
    error_message?: string;
  }) {
    this.trackEvent({
      event_name: formData.success ? 'form_submit_success' : 'form_submit_error',
      event_category: 'form_interaction',
      event_label: formData.form_name,
      custom_parameters: {
        form_location: formData.form_location,
        error_message: formData.error_message
      }
    });
  }

  // Download tracking
  trackDownload(fileName: string, fileType: string, source: string) {
    this.trackEvent({
      event_name: 'file_download',
      event_category: 'content_engagement',
      event_label: fileName,
      custom_parameters: {
        file_type: fileType,
        file_source: source
      }
    });
  }

  // Video tracking
  trackVideoEngagement(videoName: string, action: 'play' | 'pause' | 'complete', progress?: number) {
    this.trackEvent({
      event_name: `video_${action}`,
      event_category: 'video_engagement',
      event_label: videoName,
      value: progress,
      custom_parameters: {
        video_name: videoName,
        video_progress: progress
      }
    });
  }

  // Chat tracking
  trackChatInteraction(action: 'open' | 'close' | 'message_sent' | 'lead_qualified') {
    this.trackEvent({
      event_name: `chat_${action}`,
      event_category: 'chat_engagement',
      event_label: action,
      custom_parameters: {
        chat_action: action,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Scroll tracking
  trackScrollDepth(percentage: number, page: string) {
    if (percentage % 25 === 0) { // Track at 25%, 50%, 75%, 100%
      this.trackEvent({
        event_name: 'scroll_depth',
        event_category: 'user_engagement',
        event_label: `${percentage}%`,
        value: percentage,
        custom_parameters: {
          page_path: page
        }
      });
    }
  }

  // Time on page tracking with goals
  trackTimeOnPage(timeSpent: number, page: string) {
    // Define time goals by page type
    const timeGoals: { [key: string]: number } = {
      '/': 120, // Homepage: 2 minutes
      '/services': 180, // Services: 3 minutes
      '/industries': 150, // Industries: 2.5 minutes
      '/about': 90, // About: 1.5 minutes
      '/pricing': 240, // Pricing: 4 minutes
      '/contact': 60, // Contact: 1 minute
    };

    const goal = timeGoals[page] || 120;
    const goalReached = timeSpent >= goal;

    const timeCategory = timeSpent < 30 ? 'quick_visit' : 
                        timeSpent < 120 ? 'moderate_engagement' : 
                        'high_engagement';

    this.trackEvent({
      event_name: 'time_on_page',
      event_category: 'user_engagement',
      event_label: timeCategory,
      value: timeSpent,
      custom_parameters: {
        page_path: page,
        time_spent_seconds: timeSpent,
        goal_seconds: goal,
        goal_reached: goalReached
      }
    });

    // Track goal achievement separately
    if (goalReached) {
      this.trackEvent({
        event_name: 'time_goal_reached',
        event_category: 'engagement',
        event_label: page,
        value: Math.round(timeSpent),
        custom_parameters: {
          page_path: page,
          goal_seconds: goal,
          actual_seconds: timeSpent,
          percentage_of_goal: Math.round((timeSpent / goal) * 100)
        }
      });
    }
  }

  // E-commerce tracking (for future pricing/payments)
  trackPurchase(purchaseData: {
    transaction_id: string;
    value: number;
    currency: string;
    items: Array<{
      item_id: string;
      item_name: string;
      category: string;
      quantity: number;
      price: number;
    }>;
  }) {
    // GA4 purchase event
    window.gtag('event', 'purchase', {
      transaction_id: purchaseData.transaction_id,
      value: purchaseData.value,
      currency: purchaseData.currency,
      items: purchaseData.items
    });

    // Facebook Pixel purchase (check if loaded)
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        value: purchaseData.value,
        currency: purchaseData.currency,
        content_ids: purchaseData.items.map(item => item.item_id),
        content_type: 'product'
      });
    }
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Convenience functions
export const trackPageView = (path: string, title?: string) => analytics.trackPageView(path, title);
export const trackEvent = (event: AnalyticsEvent) => analytics.trackEvent(event);
export const trackLead = (leadData: Parameters<typeof analytics.trackLead>[0]) => analytics.trackLead(leadData);
export const trackFormSubmission = (formData: Parameters<typeof analytics.trackFormSubmission>[0]) => analytics.trackFormSubmission(formData);
export const trackDownload = (fileName: string, fileType: string, source: string) => analytics.trackDownload(fileName, fileType, source);
export const trackVideoEngagement = (videoName: string, action: 'play' | 'pause' | 'complete', progress?: number) => analytics.trackVideoEngagement(videoName, action, progress);
export const trackChatInteraction = (action: 'open' | 'close' | 'message_sent' | 'lead_qualified') => analytics.trackChatInteraction(action);

export default analytics;